import Head from 'next/head';
import Script from 'next/script';
import React, { createContext, useState, useEffect, useCallback } from 'react';
import useAdaAutoOpen from '../hooks/useAdaAutoOpen';

type AdaSupportContextType = {
  hide?: () => void;
  show?: () => void;
};

const AdaSupportContext = createContext<AdaSupportContextType>({} as AdaSupportContextType);

export const AdaSupportProvider = ({ children }) => {
  const [hidden, setHidden] = useState(false);
  const [adaReady, setAdaReady] = useState(false);

  const hide = useCallback(() => setHidden(true), []);
  const show = useCallback(() => setHidden(false), []);

  useAdaAutoOpen({
    paths: ['/contact'],
    adaReady,
  });

  return (
    <AdaSupportContext.Provider value={{ hide, show }}>
      <Script
        async
        src="https://static.ada.support/embed2.js"
        id="__ada"
        data-handle={process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'prezzee-gen' : 'prezzee-gen-sandbox'}
        strategy="lazyOnload"
        onLoad={() => {
          window.adaSettings = {
            adaReadyCallback: () => {
              setAdaReady(true);
            },
          };
        }}
      />
      {hidden && (
        <Head>
          <style>{`#ada-entry { display: none; }`}</style>
        </Head>
      )}

      {children}
    </AdaSupportContext.Provider>
  );
};

export const useAdaSupport = () => {
  const ctx = React.useContext(AdaSupportContext);

  if (!ctx) {
    throw new Error('useAdaSupport must be used within a AdaSupportProvider');
  }

  return ctx;
};
